import React from "react";
import { useDragPosition } from "../../hooks/useDragPosition";
import { BUBBLE_SIZE_PX } from "./dockGeometry";

/**
 * A draggable floating launcher: round icon button, optional count badge, and
 * an attention halo while the count is non-zero.
 *
 * Generic on purpose — it knows nothing about chat. Give it an icon, a count
 * and a click handler and it works for any floating surface (a support queue, a
 * task tray) that wants the same affordance.
 *
 * Drag is handled by `useDragPosition`, which tells a drag from a click; the
 * click handler consumes that flag so releasing a drag never also activates.
 *
 * @param {Object} props
 * @param {React.ComponentType<{className?: string}>} props.Icon
 * @param {number} [props.count=0] - Badge value; hidden at 0.
 * @param {string} props.label - Accessible name and tooltip.
 * @param {() => void} props.onClick
 * @param {string} props.storageKey - The dock's shared anchor. The panel reads
 *   and writes the same key, so the two always open in the same place.
 * @param {boolean} [props.isMobile=false] - Parks it and disables dragging.
 */
export function FloatingBubble({
  Icon,
  count = 0,
  label,
  onClick,
  storageKey,
  isMobile = false,
}) {
  const { style, isDragging, handleProps, consumeDragged } = useDragPosition({
    storageKey,
    width: BUBBLE_SIZE_PX,
    height: BUBBLE_SIZE_PX,
    // Resolved here rather than as module constants so the very first render
    // already knows the viewport size.
    defaultPosition: {
      left: Math.max(12, window.innerWidth - BUBBLE_SIZE_PX - 24),
      top: Math.max(12, window.innerHeight - BUBBLE_SIZE_PX - 24),
    },
    // A launcher parks itself against the nearer side; a panel wouldn't.
    snapToEdge: true,
    disabled: isMobile,
  });

  const hasCount = count > 0;

  return (
    <div
      className={`fixed z-40 ${isMobile ? "bottom-5 right-5" : ""}`}
      style={{
        ...(isMobile ? {} : style),
        width: `${BUBBLE_SIZE_PX}px`,
        height: `${BUBBLE_SIZE_PX}px`,
        // A drag must not be hijacked by the browser's own scroll/pan.
        touchAction: "none",
      }}
    >
      {/* Halo sits behind the button and is inert, so it can never eat a click. */}
      {hasCount && (
        <span
          aria-hidden="true"
          className="animate-dockHalo pointer-events-none absolute inset-0 rounded-full bg-orange-primary"
        />
      )}

      <button
        type="button"
        {...handleProps}
        onClick={() => {
          // That gesture was a drag, not a tap — don't also open.
          if (consumeDragged()) return;
          onClick();
        }}
        aria-label={label}
        title={label}
        className={`group relative flex h-full w-full items-center justify-center rounded-full bg-gradient-to-br from-[#FF8244] to-orange-primary text-white ring-1 ring-black/5 transition-transform duration-150 ease-out hover:scale-105 active:scale-95 motion-reduce:transition-none motion-reduce:hover:scale-100 ${
          isDragging
            ? "cursor-grabbing shadow-[0_8px_24px_-4px_rgba(255,100,20,0.6)]"
            : "cursor-grab shadow-[0_10px_30px_-8px_rgba(255,100,20,0.55)]"
        }`}
      >
        <Icon className="h-6 w-6" aria-hidden="true" />

        {hasCount && (
          <span
            aria-hidden="true"
            className="absolute -right-1 -top-1 flex h-[22px] min-w-[22px] items-center justify-center rounded-full bg-white px-1.5 text-[11px] font-bold text-[#C2410C] shadow-sm ring-2 ring-white"
          >
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>
    </div>
  );
}

export default FloatingBubble;
